import { ACTION_TYPE, JobBuilderContext } from 'context/jobBuilderContext';
import { Accept, FileRejection } from 'react-dropzone';
import { Grid, List, TextField, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useCallback, useContext, useEffect, memo } from 'react';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { getFileTypeForFile, MAX_FILE_SIZE_BYTES } from 'utils';
import { AppContext } from 'context/appContext';
import { EntityTypes } from 'types';
import { FileContext } from 'context/fileUploadContext';
import FileDropzone from 'components/inputs/FileDropzone';
import FileListItem from 'components/lists/listItems/FileListItem';
import { useTranslation } from 'react-i18next';

const NotesStep = (): ReactNode => {
  const { t } = useTranslation();
  const { state: jobState, dispatch } = useContext(JobBuilderContext);
  const { state } = useContext(AppContext);
  const { files, addFiles } = useContext(FileContext);
  const displayToast = useContext(ToastContext);
  const mobile = useMediaQuery(useTheme().breakpoints.down('lg'));

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  });

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles?.length) {
        addFiles(
          acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              metadata: {
                createdByUserID: state.user.id,
                tenantID: state.tenant.id,
                entityType: EntityTypes.Job,
                entityID: 0,
                fileType: getFileTypeForFile(file),
                data: {
                  title: file.name,
                  description: '',
                },
              },
              uploaded: false,
            }),
          ),
        );
      }
      if (rejectedFiles?.length) {
        rejectedFiles.forEach((r) =>
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: r.errors[0].message,
          }),
        );
      }
    },
    [],
  );

  const accept: Accept = {
    'image/*': [],
    'video/*': [],
    'application/pdf': [],
    'application/msword': [],
  };

  return (
    <Grid container spacing={1.5} sx={{ width: '100%' }} mb={{ xs: 0, sm: 2 }}>
      <Grid size={12}>
        <TextField
          multiline
          minRows={mobile ? 8 : 3}
          maxRows={mobile ? 8 : 3}
          name={'notes'}
          label={t('job.data.notes', { ns: 'entities' })}
          fullWidth
          variant='outlined'
          type='text'
          margin={mobile ? 'normal' : 'none'}
          value={jobState.notes}
          onChange={(e) => {
            dispatch({
              type: ACTION_TYPE.SET_NOTES,
              payload: e.target.value,
            });
          }}
        />
      </Grid>

      <Grid size={12}>
        <FileDropzone
          onDrop={onDrop}
          accept={accept}
          multiple={true}
          maxSize={MAX_FILE_SIZE_BYTES}
        />
        <List sx={{ mt: 2 }}>
          {files.map((file) => (
            <FileListItem key={file.name} file={file} />
          ))}
        </List>
      </Grid>
    </Grid>
  );
};

export default memo(NotesStep);
